import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

const KEY = "harvest_recommendations";

export default function HarvestThresholdsCard() {
  const { user } = useAuth();
  const { id: companyId } = useCurrentCompany();
  const queryClient = useQueryClient();

  const [minDemand, setMinDemand] = useState("Low");
  const [minPriority, setMinPriority] = useState("0");
  const [minPrice, setMinPrice] = useState("0");
  const [resalePct, setResalePct] = useState("0");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("company_settings")
        .select("setting_value")
        .eq("company_id", companyId)
        .eq("setting_key", KEY)
        .maybeSingle();
      if (!error && data?.setting_value && typeof data.setting_value === "object") {
        const rec = data.setting_value as any;
        if (typeof rec.min_demand === "string") setMinDemand(rec.min_demand);
        if (rec.min_priority != null) setMinPriority(String(rec.min_priority));
        if (rec.min_price_eur != null) setMinPrice(String(rec.min_price_eur));
        if (rec.resale_price_pct != null) setResalePct(String(rec.resale_price_pct));
      }
      setLoading(false);
    })();
  }, [companyId]);

  const handleSave = async () => {
    if (!companyId || !user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from("company_settings")
        .upsert(
          {
            company_id: companyId,
            setting_key: KEY,
            setting_value: {
              min_demand: ["High", "Medium", "Low"].includes(minDemand) ? minDemand : "Low",
              min_priority: Math.max(0, Math.min(3, Number(minPriority) || 0)),
              min_price_eur: Math.max(0, Number(minPrice) || 0),
              resale_price_pct: Math.max(0, Number(resalePct) || 0),
            },
            updated_by: user.id,
          },
          { onConflict: "company_id,setting_key" },
        );
      if (error) throw error;
      await queryClient.invalidateQueries({ queryKey: ["harvest_recommendations"] });
      toast.success("Harvest recommendations saved");
    } catch (err: any) {
      toast.error(err.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <section className="bg-card rounded-xl border border-border p-5 space-y-4">
      <div>
        <h2 className="font-semibold text-foreground">Harvest recommendations</h2>
        <p className="text-sm text-muted-foreground">
          Thresholds passed to the recommendation engine when picking spare parts to harvest.
          Parts that don't meet these are excluded.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="rec_demand">Minimum demand</Label>
          <select
            id="rec_demand"
            value={minDemand}
            onChange={(e) => setMinDemand(e.target.value)}
            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rec_priority">Minimum harvest priority (0–3)</Label>
          <Input
            id="rec_priority"
            type="number"
            min={0}
            max={3}
            step={1}
            value={minPriority}
            onChange={(e) => setMinPriority(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rec_price">Minimum price (€)</Label>
          <Input
            id="rec_price"
            type="number"
            min={0}
            step="0.01"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="rec_resale">Resale price percentage (%)</Label>
          <Input
            id="rec_resale"
            type="number"
            min={0}
            step="0.1"
            value={resalePct}
            onChange={(e) => setResalePct(e.target.value)}
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="gap-2">
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </div>
    </section>
  );
}
